import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Compass, Gem } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal, Eyebrow } from "../components/Reveal";

const TEAM_IMG = "/agence-devanture.jpg";

const CHAPTERS = [
  {
    n: "01",
    title: "Notre histoire",
    text: "Après trois années d'expérience en agence immobilière sur la Côte d'Azur, Mehdi El Agrebi fonde Kemelia Estate avec une volonté simple : remettre l'humain au cœur de l'immobilier. Une expertise forgée sur le terrain, aujourd'hui mise au service d'une agence indépendante où chaque projet reçoit une attention totale.",
  },
  {
    n: "02",
    title: "Pourquoi la Côte d'Azur",
    text: "Parce que nous y vivons et la parcourons chaque semaine. De la Croisette aux criques du Cap d'Antibes, des hauteurs de Mougins à la Baie des Anges : notre connaissance du secteur est d'abord une connaissance du terrain, rue par rue, vue par vue.",
  },
  {
    n: "03",
    title: "Notre vision",
    text: "Une agence à taille humaine n'a pas vocation à vendre plus, mais à accompagner mieux. Moins de mandats, plus d'attention. Moins de promesses, plus de résultats. C'est cette exigence discrète qui fait notre réputation — et la confiance de nos clients.",
  },
  {
    n: "04",
    title: "Un réseau qui travaille pour vous",
    text: "Promoteurs dans le neuf, commerces de proximité, notaires, courtiers : notre réseau local, construit au fil des années, ouvre à nos clients des opportunités souvent inaccessibles autrement — y compris des biens off-market que vous ne verrez nulle part ailleurs.",
  },
];

const VALUES = [
  { icon: HeartHandshake, title: "Proximité", text: "Un interlocuteur unique qui connaît votre dossier sur le bout des doigts." },
  { icon: Compass, title: "Exigence", text: "Chaque bien sélectionné est visité, estimé et défendu avec rigueur." },
  { icon: Gem, title: "Discrétion", text: "Une approche confidentielle, y compris pour les ventes off-market." },
];

export default function Agence() {
  return (
    <>
      <PageHeader
        eyebrow="Notre agence"
        title="Une autre façon de faire de l'immobilier."
        sub="Ni franchise, ni call-center : une équipe qui vous connaît par votre prénom."
      />

      <section data-testid="agence-story" className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
        <div className="grid items-start gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="lg:sticky lg:top-28">
            <div className="overflow-hidden rounded-2xl">
              <img
                src={TEAM_IMG}
                alt="La devanture de l'agence Kemelia Estate"
                data-testid="agence-team-image"
                className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105 sm:h-[540px]"
              />
            </div>
            <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
              Notre agence — Côte d'Azur
            </p>
          </Reveal>

          <div>
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="border-t border-slate-200 py-10 first:border-t-0 first:pt-0">
                  <span className="outline-number font-serif text-5xl font-light">{c.n}</span>
                  <h2 className="mt-4 font-serif text-2xl font-semibold text-azur sm:text-3xl">{c.title}</h2>
                  <p className="mt-4 font-light leading-relaxed text-slate-600">{c.text}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.2}>
              <div className="mt-4 rounded-2xl border border-gold/25 bg-white p-7 shadow-[0_4px_20px_rgba(0,0,0,0.03)]">
                <p className="font-serif text-xl italic text-azur">
                  Mehdi El Agrebi — Fondateur
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {[
                    "3 ans d'expérience en agence",
                    "Réseau promoteurs du neuf",
                    "Commerces & partenaires locaux",
                  ].map((chip) => (
                    <span key={chip} className="rounded-full bg-sand px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-azur">
                      {chip}
                    </span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-24">
          <Reveal>
            <Eyebrow>Nos valeurs</Eyebrow>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="group h-full rounded-2xl border border-slate-200/80 bg-white p-8 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_12px_40px_rgba(12,35,64,0.08)]">
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-azur text-sand transition-colors duration-500 group-hover:bg-terracotta">
                    <v.icon size={19} />
                  </span>
                  <h3 className="mt-5 font-serif text-xl font-semibold text-azur">{v.title}</h3>
                  <p className="mt-2 text-sm font-light leading-relaxed text-slate-600">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal className="mt-24">
          <div className="flex flex-col items-start justify-between gap-8 rounded-3xl bg-azur px-8 py-12 sm:px-12 lg:flex-row lg:items-center">
            <div>
              <h2 className="font-serif text-2xl font-normal text-sand sm:text-3xl">
                Envie d'en parler autour d'un café ?
              </h2>
              <p className="mt-2 font-light text-sand/70">
                Rencontrons-nous à l'agence, chez vous, ou en visio — à votre rythme.
              </p>
            </div>
            <Link
              to="/acheter"
              data-testid="agence-cta-contact"
              className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-medium text-azur transition-colors duration-300 hover:bg-sand"
            >
              Discuter de votre projet
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
